import { HTMLUtils } from "../utils/HTMLUtils";
import { Field } from "../utils/ui/Field";
import { Main } from "../Main";

/**
 * You should use one of the descendants of this class: WarningWindow, ConfirmWindow, or PromptWindow
 */

export abstract class PopupWindow<T>
{
	private _background: HTMLElement;
	private _domElement: HTMLDivElement;
	protected _additionalElements: HTMLElement[] = [];
	private _buttonContainer: HTMLDivElement;
	private _okElement: HTMLDivElement;
	private _cancelElement: HTMLDivElement;

	private _title: string;
	private _message: string;
	private _ok: string;
	private _cancel: string;
	private _backdrop: boolean;
	private _onClickDrop: boolean;
	private _className: string;

	protected static isPopupActive: boolean = false;
	private _inputFieldPairs: {field: Field, isDisabled: boolean}[];

	protected okValue: T;
	protected cancelValue: T;

	protected resolve: (isOk?: T) => any;

	constructor(title: string, message: string, backdrop: boolean = true, ok: string = "Yes", cancel: string = "No", onClickDrop: boolean = false, className?: string)
	{
		this._title = title;
		this._message = message;
		this._ok = ok;
		this._cancel = cancel;
		this._backdrop = backdrop;
		this._onClickDrop = onClickDrop;
		this._inputFieldPairs = [];
		this._className = className;
	}

	private addContent = (title: string, message: string) =>
	{
		this._domElement = document.createElement("div");
		this._domElement.classList.add("popupWindow");

		if (this._className)
		{
			this._domElement.classList.add(this._className);
		}

		const titleElement = document.createElement("div");
		titleElement.textContent = title;
		titleElement.classList.add("title");

		const messageElement = document.createElement("div");
		messageElement.innerHTML = message;
		messageElement.classList.add("message");

		this._domElement.appendChild(titleElement);
		this._domElement.appendChild(messageElement);

		this.addAdditionalElements(this._additionalElements)

		document.body.appendChild(this._domElement);
	};

	private addAdditionalElements(elements: HTMLElement[])
	{
		for (const element of elements)
		{
			this._domElement.appendChild(element);
		}
	}

	private addButtons = (ok: string, cancel: string) =>
	{
		this._buttonContainer = document.createElement("div");
		this._buttonContainer.classList.add("buttonContainer");

		this._okElement = document.createElement("div");
		this._okElement.textContent = ok;
		this._okElement.classList.add("button");
		this._okElement.classList.add("ok");
		this._okElement.setAttribute("isOkButtonOnPopup", "true");
		this._okElement.onclick = () =>
		{
			this.okClicked();
		};

		window.addEventListener("keyup", this.keyUp);
		if (ok)
		{
			this._buttonContainer.appendChild(this._okElement);
		}

		if (cancel)
		{
			this._cancelElement = document.createElement("div");
			this._cancelElement.textContent = cancel;
			this._cancelElement.classList.add("button");
			this._cancelElement.classList.add("cancel");
			this._cancelElement.onclick = () =>
			{
				this.cancelClicked();
			};
			this._buttonContainer.appendChild(this._cancelElement);
		}

		this._domElement.appendChild(this._buttonContainer);
	};

	private keyUp = (event: KeyboardEvent) =>
	{
		switch(event.key)
		{
			case "Enter":
				this.okClicked();
				break;
			case "Escape":
				this.cancelClicked();
				break;
		}
	};

	private disableSetOfFields(fields: {[id: string]: Field})
	{
		for (const fieldName in fields)
		{
			const field = fields[fieldName];
			this._inputFieldPairs.push({field: field, isDisabled: field.isDisabled()});
			field.disable();
		}
	}

	/**
	 * Disables inputfields to prevent multiple popupwindows from appearing at once
	 */
	private disableInputFields()
	{
		this._inputFieldPairs.length = 0;
		const buildingSizeFields = Main.getInstance().toolsView.buildingSize.fields;
		const overhangFields = Main.getInstance().toolsView.overhang.fields;
		this.disableSetOfFields(buildingSizeFields);
		this.disableSetOfFields(overhangFields);
	}

	/**
	 * Enables the inputfield that were originally enabled
	 */
	private enableInputFields()
	{
		for (const inputFieldPair of this._inputFieldPairs)
		{
			if (!inputFieldPair.isDisabled)
			{
				inputFieldPair.field.enable();
			}
		}
		this._inputFieldPairs.length = 0;
	}

	protected init()
	{
		this._background = document.getElementById("popupBackground");
		this._background.classList.remove("hidden");

		this.addContent(this._title, this._message);
		this.addButtons(this._ok, this._cancel);

		if (this._backdrop)
		{
			this._background.onclick = () =>
			{
				this.cancelClicked();
			};
		}

		if (this._onClickDrop)
		{
			this._domElement.onclick = () =>
			{
				this.cancelClicked();
			};
		}
	}

	protected close()
	{
		this._background.classList.add("hidden");
		HTMLUtils.clearElement(this._buttonContainer, true);
		HTMLUtils.clearElement(this._domElement, true);
		window.removeEventListener("keyup", this.keyUp);
		this.enableInputFields();
		PopupWindow.isPopupActive = false;
	}

	public open()
	{
		if (!PopupWindow.isPopupActive)
		{
			PopupWindow.isPopupActive = true;
			this.disableInputFields();

			return new Promise<T>((resolve, reject) =>
			{
				this.resolve = resolve;
				this.init();
			});
		}
		else
		{
			return null;
		}
	}

	private cancelClicked()
	{
		this.close();
		this.resolve(this.cancelValue);
	}

	protected okClicked()
	{
		this.close();
		this.resolve(this.okValue);
	}
}